import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ShopperAiTagId,
} from "../../../ids/definitions";
import { MongoCustomerId, MongoUserId } from "../../../ids/mongo-ids";
import { CustomerBasicInfo } from "../../../models/customer";
import { NullableDateFromDb } from "../../../schema/helpers";
import { ShopperAiIntegrationType } from "../integration/integration";

export const CONVERSATION_STATUSES = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const;
export const ConversationStatus = Schema.Literal(...CONVERSATION_STATUSES);
export type ConversationStatus = typeof ConversationStatus.Type;

export const ConversationPriority = Schema.Literal(
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
);
export type ConversationPriority = typeof ConversationPriority.Type;

export const ConversationSnoozeOptions = Schema.Literal(
  "1h",
  "4h",
  "24h",
  "custom",
);
export type ConversationSnoozeOptions = typeof ConversationSnoozeOptions.Type;

export const ConversationMode = Schema.Literal("DEMO", "LOCAL", "PROD");
export type ConversationMode = typeof ConversationMode.Type;

export const CONVERSATION_CLOSURE_REASONS = [
  "EXPIRED",
  "RESOLVED",
  "MANUAL",
  "SPAM",
  "DUPLICATE",
] as const;
export const ConversationClosureReason = Schema.Literal(
  ...CONVERSATION_CLOSURE_REASONS,
);
export type ConversationClosureReason = typeof ConversationClosureReason.Type;

export const CustomerSentiment = Schema.Literal(
  "POSITIVE",
  "NEUTRAL",
  "NEGATIVE",
);
export type CustomerSentiment = typeof CustomerSentiment.Type;

export const AssigneeSummary = Schema.Literal(
  "AI_AGENT_HANDLED",
  "MERCHANT_HANDLED",
  "AI_AGENT_HANDOFF_TO_HUMAN",
  "AI_AGENT_HANDOFF_TO_IGNORED",
  "MERCHANT_IGNORED",
);
export type AssigneeSummary = typeof AssigneeSummary.Type;

export const ConversationResponse = Schema.Struct({
  id: ConversationId,
  accountId: AccountId,
  status: ConversationStatus,
  mode: ConversationMode,
  priority: Schema.optional(ConversationPriority),
  mongoCustomerId: MongoCustomerId,
  mongoAssigneeId: Schema.NullOr(MongoUserId),
});

export type ConversationResponse = typeof ConversationResponse.Type;

export const ConversationListItemResponse = Schema.Struct({
  id: ConversationId,
  accountId: AccountId,
  status: ConversationStatus,
  priority: Schema.NullOr(ConversationPriority),
  mongoCustomerId: MongoCustomerId,
  mongoAssigneeId: Schema.NullOr(MongoUserId),
  readAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  summary: Schema.NullOr(Schema.String),
  customerSentiment: Schema.NullOr(CustomerSentiment),
  assigneeSummary: Schema.NullOr(AssigneeSummary),
  channels: Schema.Array(ShopperAiIntegrationType),
  customer: Schema.optional(CustomerBasicInfo),
  tagIds: Schema.Array(ShopperAiTagId),
  createdAt: Schema.NullOr(Schema.Date),
  statusUpdatedAt: Schema.NullOr(Schema.Date),
});
export type ConversationListItemResponse =
  typeof ConversationListItemResponse.Type;

/**
 * Type guard for ConversationListItemResponse.
 * Validates all required fields with their expected types.
 */
export function isConversationListItem(
  context: unknown,
): context is ConversationListItemResponse {
  if (context === null || typeof context !== "object") {
    return false;
  }

  const obj = context as Record<string, unknown>;

  // Check required string fields
  if (
    typeof obj.id !== "string" ||
    typeof obj.accountId !== "string" ||
    typeof obj.mongoCustomerId !== "string"
  ) {
    return false;
  }

  // Check status is a valid ConversationStatus
  if (
    typeof obj.status !== "string" ||
    !CONVERSATION_STATUSES.includes(obj.status as ConversationStatus)
  ) {
    return false;
  }

  // Check nullable date fields
  const isNullOrDate = (val: unknown): boolean =>
    val === null || val instanceof Date;

  if (
    !isNullOrDate(obj.readAt) ||
    !isNullOrDate(obj.lastCustomerMessageAt) ||
    !isNullOrDate(obj.lastMerchantMessageAt)
  ) {
    return false;
  }

  // Check nullable string fields
  const isNullOrString = (val: unknown): boolean =>
    val === null || typeof val === "string";

  if (!isNullOrString(obj.summary)) {
    return false;
  }

  // Check tagIds is an array
  if (!Array.isArray(obj.tagIds)) {
    return false;
  }

  return true;
}

export const ConversationListCursor = Schema.Struct({
  lastCustomerMessageAt: Schema.NullOr(Schema.DateFromString),
  id: ConversationId,
});
export type ConversationListCursor = typeof ConversationListCursor.Type;

export const UserTicketStats = Schema.Struct({
  userId: MongoUserId.fromDbBuffer,
  openTicketCount: Schema.NumberFromString.pipe(
    Schema.int(),
    Schema.nonNegative(),
  ),
  mostRecentTicketDate: NullableDateFromDb,
});
export type UserTicketStats = typeof UserTicketStats.Type;

/**
 * Discriminated union for tag update operations on a conversation.
 * - "replace": Full replacement of all tags (deletes unspecified, upserts specified)
 * - "add": Additive — only upserts the given tags, leaves others untouched
 * - "remove": Subtractive — only removes the given tags, leaves others untouched
 */
export const ConversationTagUpdate = Schema.Union(
  Schema.Struct({
    _tag: Schema.Literal("replace"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
  Schema.Struct({
    _tag: Schema.Literal("add"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
  Schema.Struct({
    _tag: Schema.Literal("remove"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
);
export type ConversationTagUpdate = typeof ConversationTagUpdate.Type;

export const UpdateConversationInput = Schema.Struct({
  status: Schema.optional(ConversationStatus),
  priority: Schema.optional(Schema.OptionFromNullOr(ConversationPriority)),
  readAt: Schema.optional(Schema.OptionFromNullOr(Schema.Date)),
  mongoAssigneeId: Schema.optional(Schema.OptionFromNullOr(MongoUserId)),
  tagUpdate: Schema.optional(ConversationTagUpdate),
});
export type UpdateConversationInput = typeof UpdateConversationInput.Type;
